import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/corezoidApi';
import getApiBaseUrl from '../utils/apiBaseUrl';
import './BalanceBar.css';

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_GATEWAY_CURRENCY = 'USDT';

const BalanceBar = ({ compact = false }) => {
  const { isAuthenticated, user } = useAuth();
  const isGatewayUser = user?.role === 'gateway';
  const [balance, setBalance] = useState({
    total: '0',
    pending: '0',
    current: '0'
  });
  const [gatewayBalance, setGatewayBalance] = useState('0');
  const [gatewayCurrency, setGatewayCurrency] = useState(DEFAULT_GATEWAY_CURRENCY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchBalance = async () => {
      try {
        const response = await apiClient.get(`${API_BASE_URL}/auth/balance`);
        if (response.data.success) {
          if (isGatewayUser) {
            setGatewayBalance(String(response.data.balance ?? '0'));
            setGatewayCurrency(response.data.currency || DEFAULT_GATEWAY_CURRENCY);
          } else if (response.data.balance) {
            setBalance(response.data.balance);
          }
        }
      } catch (error) {
        // In case of an error, keep zeros
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
    
    // Update the balance every 30 seconds
    const interval = setInterval(fetchBalance, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, isGatewayUser]);

  const formatBalance = (value, currency = 'USDT') => {
    const num = parseFloat(value) || 0;
    const formatted = num.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    return `${formatted} ${currency}`;
  };

  if (loading) {
    return (
      <div className={`balance-bar ${compact ? 'compact' : ''}`}>
        <div className="balance-header">
          <h3 className="balance-title">Balance</h3>
        </div>
        <div className="balance-items">
          {isGatewayUser ? (
            <div className="balance-item">
              <span className="balance-label">BALANCE</span>
              <span className="balance-value">... {gatewayCurrency}</span>
            </div>
          ) : (
            <>
              <div className="balance-item">
                <span className="balance-label">ALL</span>
                <span className="balance-value">... USDT</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">PENDING</span>
                <span className="balance-value pending">... USDT</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">CURRENT</span>
                <span className="balance-value current">... USDT</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`balance-bar ${compact ? 'compact' : ''}`}>
      <div className="balance-header">
        <h3 className="balance-title">Balance</h3>
      </div>
      <div className="balance-items">
        {isGatewayUser ? (
          <div className="balance-item">
            <span className="balance-label">BALANCE</span>
            <span className="balance-value">{formatBalance(gatewayBalance, gatewayCurrency)}</span>
          </div>
        ) : (
          <>
            <div className="balance-item">
              <span className="balance-label">ALL</span>
              <span className="balance-value">{formatBalance(balance.total)}</span>
            </div>
            <div className="balance-item">
              <span className="balance-label">PENDING</span>
              <span className="balance-value pending">{formatBalance(balance.pending)}</span>
            </div>
            <div className="balance-item">
              <span className="balance-label">CURRENT</span>
              <span className="balance-value current">{formatBalance(balance.current)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BalanceBar;
